<template>
  <div
    class="champ-card"
    :class="{
      'champ-card_bordered': bordered,
      'champ-card_draft': draft
    }"
  >
    <button
      class="champ-card__delete link link_light"
      type="button"
      v-if="draft"
      :disabled="deleting"
      @click.prevent="deleteChamp"
    >
      <icon class="icon" icon-name="delete" w="16" h="16" v-show="!deleting"></icon>
      <div class="loader m-0" v-show="deleting"></div>
    </button>
    <div class="champ-card__container">
      <div
        v-if="!internal"
        class="champ-card__cover"
      >
        <div
          class="champ-card__cover-img"
          :class="{'champ-card__cover-img_default': !hasImage}"
          v-show="image"
          :style="{ backgroundImage: `url(${image})` }"
        >
        </div>
        <a
          class="champ-card__cover-link"
          :href="champLink"
          target="_blank"
        ></a>
      </div>
      <div class="champ-card__info">
        <div class="champ-card__header">
          <div class="champ-card__header-col">
            <b class="color-primary" v-html="date"></b>
          </div>
        </div>
        <div class="champ-card__body">
          <a
            v-if="!manage"
            class="champ-card__name"
            v-html="name"
            :href="champLink"
            target="_blank"
          ></a>
          <router-link
            v-else
            class="champ-card__name"
            v-html="name"
            :to="draft ? { name: 'CreateChamp', params: { id: $route.params.id, champ: champ.Id } } : { name: 'EditChamp', params: { champId: champ.Id } }"
          ></router-link>
        </div>
        <div class="champ-card__footer">
          <div class="champ-card__footer-item" v-if="discipline">
            <div class="details details_sm text-xs">
              <div class="details__item">
                <div class="text-with-icon">
                  <icon v-if="discipline.icon" class="icon" :icon-name="discipline.icon" w="12" h="12"></icon>
                  <span>{{ discipline.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            class="champ-card__footer-item champ-card__footer-item_main"
            v-show="draft || nominationsCount || (finished && participantsCount > 0) || isMember || (!finished && !hideStatus)"
          >
            <div class="details text-sm">
              <div class="details__item" v-if="nominationsCount">
                <b class="text-with-icon">
                  <icon class="icon" icon-name="trophy" w="16" h="16"></icon>
                  <span>{{ nominationsCount }} номинаци{{ getSuffix(nominationsCount, ['я', 'и', 'й']) }}</span>
                </b>
              </div>
              <div class="details__item" v-if="finished && participantsCount > 0">
                <b class="text-with-icon">
                  <icon class="icon" icon-name="user-groups" w="16" h="16"></icon>
                  <span>{{ participantsCount }}</span>
                </b>
              </div>
              <div class="details__item" v-if="isMember">
                <b class="text-with-icon color-success">
                  <icon class="icon" icon-name="check-bold" w="16" h="16"></icon>
                  <span>Вы участник</span>
                </b>
              </div>
              <div
                class="details__item"
                v-else-if="!finished && !hideStatus"
              >
                <b class="text-with-icon" v-if="internal">
                  <icon class="icon" icon-name="lock" w="16" h="16"></icon>
                  <span>Для участников клуба</span>
                </b>
                <b class="text-with-icon" v-else>
                  <icon class="icon" icon-name="padlock" w="16" h="16"></icon>
                  <span>Открытый</span>
                </b>
              </div>
              <div class="details__item w-100" v-if="draft">
                <b class="text-with-icon color-warning">
                  <icon class="icon" icon-name="edit" w="16" h="16"></icon>
                  <span>Черновик</span>
                </b>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="champ-card__result" v-if="internal && isMember">
        <div class="champ-card__result-title">
          {{ finished ? 'Ваш результат' : 'Вы в&nbsp;рейтинге' }}
        </div>
        <div class="champ-card__metrics">
          <div class="champ-card__metrics-row">
            <div
              class="champ-card__metrics-item"
              v-for="item in rating"
              :key="item.NominationId"
            >
              <metrics-item
                :name="`«${item.NominationName}»`"
                :value="`${item.Position}-й`"
              ></metrics-item>
            </div>
          </div>
        </div>
      </div>
      <div
        class="champ-card__actions"
        v-if="!draft"
      >
        <div class="champ-card__actions-item">
          <Progress
            :value="progressValue"
            :total="progressTotal"
          >
            <template slot="caption">Этапов завершено:</template>
          </Progress>
        </div>
        <div class="champ-card__actions-item">
          <div
            class="champ-card__stage color-inherit h3-m m-0"
            v-if="finished"
          >
            Завершён
          </div>
          <template v-else-if="stagesLeft">
            <div class="champ-card__stage h3-m m-0">
              {{ stagesLeft }}&nbsp;этап{{ getSuffix(stagesLeft, ['', 'а', 'ов']) }}
            </div>
            <div class="champ-card__status">осталось до&nbsp;конца</div>
          </template>
        </div>
        <div class="champ-card__actions-item">
          <a
            class="button button_secondary button_wide"
            :href="resultsLink"
            target="_blank"
            v-if="finished"
          >
            <span class="button__text">Результаты</span>
          </a>
          <a
            class="button button_secondary button_wide"
            :href="resultsLink"
            target="_blank"
            v-else-if="isMember && internal"
          >
            <span class="button__text">Статистика</span>
          </a>
          <a
            class="button button_secondary button_wide"
            :href="clubStatsLink"
            target="_blank"
            v-else-if="!internal"
          >
            <span class="button__text">Статистика клуба</span>
          </a>
          <a
            class="button button_secondary button_wide"
            :href="resultsLink"
            target="_blank"
            v-else-if="internal"
          >
            <span class="button__text">Рейтинг участников</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Progress from '@/components/Progress.vue'
import MetricsItem from '@/components/MetricsItem'
import { getSuffix, formatDateRange, getCroppedImg } from '@/assets/js/helpers'
import { sportTypesImagesMixin } from '@/assets/js/mixins'
import { mapState, mapActions } from 'vuex'
import champsService from '@/services/champsService'

export default {
  name: 'ChampCard',
  components: {
    Progress,
    MetricsItem
  },
  mixins: [sportTypesImagesMixin],
  props: {
    clubId: String,
    bordered: Boolean,
    internal: Boolean,
    draft: Boolean,
    manage: Boolean,
    champ: Object,
    hideStatus: Boolean,
    hideMember: Boolean
  },
  data () {
    return {
      code: null,
      name: null,
      date: null,
      stagesTotal: 0,
      stagesFinished: 0,
      disciplineCode: null,
      nominationsCount: 0,
      image: null,
      hasImage: false,
      rating: [],
      participantsCount: 0,
      deleting: false
    }
  },
  computed: {
    ...mapState('user', ['user']),
    progressValue () {
      if (this.champ && this.stagesTotal < 1) {
        return this.finished ? 1 : 0
      }

      return this.stagesFinished
    },
    progressTotal () {
      return this.stagesTotal || 1
    },
    stagesLeft () {
      return this.progressTotal - this.progressValue
    },
    discipline () {
      const discipline = this.disciplineCode && this.sportTypesImages[this.disciplineCode]

      if (!discipline) {
        return null
      }

      return {
        icon: discipline.icon,
        name: this.disciplineName
      }
    },
    isMember () {
      return !this.hideMember && this.champ.CurrentUserIsMember
    },
    finished () {
      return this.champ.Status === 3
    },
    champLink () {
      if (!this.draft) {
        return this.$getEnvVar('VUE_APP_CHAMPS_URL') + `Championship/${this.code}`
      }
      return null
    },
    resultsLink () {
      return this.$getEnvVar('VUE_APP_CHAMPS_URL') + `Championship/${this.code}/NominationsAndLeaders`
    },
    userStatsLink () {
      return this.$getEnvVar('VUE_APP_CHAMPS_URL') + `Championship/${this.code}/Participant?participantId=${this.user.id}`
    },
    clubStatsLink () {
      return this.$getEnvVar('VUE_APP_CHAMPS_URL') + `Championship/${this.code}/NominationsAndLeaders`
    },
    joinLink () {
      return this.$getEnvVar('VUE_APP_CHAMPS_URL') + `Championship/${this.code}`
    }
  },
  methods: {
    ...mapActions('alerts', ['addSomethingWrongAlert']),
    getSuffix,
    setData () {
      const champ = this.champ

      if (!champ) {
        return
      }

      this.code = this.champ.Code || this.champ.Id
      this.name = champ.Name
      this.date = formatDateRange([
        this.$moment(champ.StartDate),
        this.$moment(champ.FinishDate)
      ])
      this.stagesTotal = champ.TotalStagesCount
      this.stagesFinished = champ.CompletedStagesCount
      this.disciplineCode = champ.DisciplineCode
      this.disciplineName = champ.DisciplineName
      this.nominationsCount = champ.NominationsCount
      this.rating = this.champ.CurrentUserPositions
      this.participantsCount = this.champ.ParticipansCount

      this.setImage()
    },
    setImage () {
      const url = getCroppedImg(this.champ.ImageUrl, [null, 400])
      const img = new Image()
      const imgUrl = '/Content/images/championship/championship-background.jpg'

      img.onload = () => {
        this.image = url
        this.hasImage = true
      }
      img.onerror = () => {
        this.image = `${this.$getEnvVar('VUE_APP_CHAMPS_URL')}${imgUrl}`
      }
      img.src = url
    },
    deleteChamp () {
      if (this.deleting) {
        return
      }

      this.$root.confirmDialog(
        {
          title: 'Вы уверены, что хотите удалить черновик?',
          confirmText: 'Удалить'
        },
        () => {
          this.deleting = true

          setTimeout(() => {
            champsService.deleteChamp({
              champId: this.champ.Id
            })
              .then((res) => {
                const data = res.data

                if (data.Success) {
                  this.$emit('deleted', data.ChampionshipId)
                } else {
                  this.deleting = false
                  this.addSomethingWrongAlert()
                }
              })
              .catch(() => {
                this.deleting = false
                this.addSomethingWrongAlert()
              })
          }, 200)
        }
      )
    }
  },
  created () {
    this.setData()
  }
}
</script>

<style lang="scss">
  .champ-card {
    display: flex;
    height: 100%;
    @include base-sm-shadow;
    transition: $transition-default;
    transition-property: box-shadow, border-color;
    position: relative;

    .no-touch &:hover {
      box-shadow: $elevation-deth-8;
    }

    &_bordered {
      border: 2px solid map-get($color-neutral-black, 15);
      padding: 2px;
      box-shadow: none;

      .no-touch &:hover {
        box-shadow: none;
        border-color: map-get($color-neutral-black, 25);
      }
    }

    &_draft {
      border-color: map_get($color-system-warning, 5);

      .no-touch &:hover {
        box-shadow: none;
        border-color: map-get($color-system-warning, 5);
      }
    }

    &__container {
      display: flex;
      flex-direction: column;
      width: 100%;

      @media (min-width: breakpoint(md)) {
        min-height: 200px;
      }

      @media (min-width: breakpoint(lg)) {
        flex-direction: row;
      }
    }

    &__delete {
      position: absolute;
      top: 0;
      right: 0;
      padding: 8px;
      margin: 1px;

      .loader {
        width: 16px;
        height: 16px;
        min-height: 16px;

        &::before {
          width: 100%;
          height: 100%;
          margin: 0 -8px;
        }
      }
    }

    &__cover {
      flex: 0 0 auto;
      height: 176px;
      position: relative;
      border-radius: 4px 4px 0 0;
      background-color: $bg-light;

      @media (min-width: breakpoint(lg)) {
        width: 200px;
        height: auto;
        border-radius: 4px 0 0 4px;
      }

      .champ-card_bordered & {
        border-radius: 0;
      }
    }

    &__cover-img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-size: cover;
      background-position: top center;
      border-radius: inherit;
      animation: fadein 0.15s;

      &_default {
        background-position: top left -95px;
      }
    }

    &__cover-link {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 4px;
    }

    &__header-col {
      flex: 0 0 auto;
      max-width: 100%;
    }

    &__info {
      min-width: 0;
      display: flex;
      flex-direction: column;
      padding: 12px 16px 16px;
      margin-bottom: auto;
      flex-grow: 1;

      @media (min-width: breakpoint(md)) {
        padding: 16px 20px;
      }

      @media (min-width: breakpoint(lg)) {
        flex: 1 1 100%;
        margin-bottom: 0;
      }
    }

    &__name {
      display: block;
      @include text-lg;
      font-weight: $font-weight-semibold;
      color: inherit;
      text-decoration: none;
      transition: $transition-default;

      .no-touch &:hover {
        color: $color-primary;
      }
    }

    &__body {
      margin-bottom: 16px;
    }

    &__footer {
      margin: 0 -8px -16px;
      margin-top: auto;
    }

    &__footer-item {
      flex: 0 0 auto;
      max-width: 100%;
      min-width: 0;
      padding: 0 8px;
      margin-bottom: 16px;

      &_main {
        flex: 1 1 100%;
      }
    }

    &__location {
      @include text-sm;
      white-space: nowrap;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1 1 100%;
    }

    &__result {
      display: flex;
      flex-direction: column;
      flex: 0 0 auto;
      max-width: 100%;
      background-color: $color-primary-1;
      text-align: center;
      border-radius: 2px;
      margin-bottom: 16px;
      padding: 12px;

      @media (min-width: breakpoint(lg)) {
        width: 192px;
        margin: 16px 0;
      }
    }

    &__result-title {
      @include text-sm;
      color: $color-primary;
      font-weight: $font-weight-semibold;
      margin-bottom: 8px;
    }

    &__metrics-row {
      display: flex;
      margin-right: -8px;
      margin-bottom: -8px;

      @media (min-width: breakpoint(lg)) {
        display: block;
      }
    }

    &__metrics-item {
      flex: 0 1 100%;
      margin-right: 8px;
      margin-bottom: 8px;
    }

    &__actions {
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      justify-content: space-between;
      margin-bottom: -16px;
      padding: 0 16px 16px;
      text-align: center;

      @media (min-width: breakpoint(lg)) {
        width: 245px + 32px;
        padding-top: 16px;
      }
    }

    &__actions-item {
      margin-bottom: 16px;
      line-height: 0;
    }

    &__stage {
      color: $color-primary;
    }

    &__status {
      @include text-xs;
      margin-top: 4px;
    }
  }

  .stars {
    @include text-sm;
    font-weight: $font-weight-semibold;
  }
</style>
